<template>
  <!-- Context Menu -->
  <q-menu
    v-if="contextMenuTarget"
    v-model="showContextMenu"
    :target="contextMenuTarget"
    context-menu
    auto-close
    transition-show="jump-down"
    transition-hide="jump-up"
  >
    <q-list dense style="min-width: 200px">
      <!-- Building Context Menu Options -->
      <template v-if="contextMenuNode?.type === 'building'">
        <q-item
          v-close-popup
          clickable
          @click="emit('add-level', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="add" color="primary" />
          </q-item-section>
          <q-item-section>Add Level</q-item-section>
        </q-item>

        <q-item
          v-close-popup
          clickable
          @click="emit('edit-building', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="edit" color="info" />
          </q-item-section>
          <q-item-section>Edit Building</q-item-section>
        </q-item>

        <q-separator />

        <q-item
          v-close-popup
          clickable
          @click="emit('delete-building', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="delete" color="negative" />
          </q-item-section>
          <q-item-section>Delete Building</q-item-section>
        </q-item>
      </template>

      <!-- Level Context Menu Options -->
      <template v-if="contextMenuNode?.type === 'level'">
        <q-item
          v-close-popup
          clickable
          @click="emit('add-space', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="add" color="primary" />
          </q-item-section>
          <q-item-section>Add Space</q-item-section>
        </q-item>

        <q-item
          v-close-popup
          clickable
          @click="emit('edit-level', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="edit" color="info" />
          </q-item-section>
          <q-item-section>Edit Level</q-item-section>
        </q-item>

        <q-separator />

        <q-item
          v-close-popup
          clickable
          @click="emit('delete-level', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="delete" color="negative" />
          </q-item-section>
          <q-item-section>Delete Level</q-item-section>
        </q-item>
      </template>

      <!-- Space Context Menu Options -->
      <template v-if="contextMenuNode?.type === 'space'">
        <q-item
          v-close-popup
          clickable
          @click="emit('add-equipment-to-space', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="precision_manufacturing" color="primary" />
          </q-item-section>
          <q-item-section>Add Equipment</q-item-section>
        </q-item>

        <q-item
          v-close-popup
          clickable
          @click="emit('add-compound-template', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="dashboard_customize" color="purple" />
          </q-item-section>
          <q-item-section>Add Compound from Template</q-item-section>
        </q-item>

        <q-item
          v-close-popup
          clickable
          @click="emit('add-switchboard', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="electrical_services" color="red" />
          </q-item-section>
          <q-item-section>Add Switchboard</q-item-section>
        </q-item>

        <q-item
          v-close-popup
          clickable
          @click="emit('edit-space', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="edit" color="info" />
          </q-item-section>
          <q-item-section>Edit Space</q-item-section>
        </q-item>

        <q-separator />

        <q-item
          v-close-popup
          clickable
          @click="emit('delete-space', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="delete" color="negative" />
          </q-item-section>
          <q-item-section>Delete Space</q-item-section>
        </q-item>
      </template>

      <!-- Zone Context Menu Options (Equipment-based zones) -->
      <template v-if="isZone(contextMenuNode)">
        <q-item
          v-close-popup
          clickable
          @click="emit('delete-equipment', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="delete" color="negative" />
          </q-item-section>
          <q-item-section>Delete Zone</q-item-section>
        </q-item>
      </template>

      <!-- Point Context Menu Options -->
      <template v-if="contextMenuNode?.type === 'point'">
        <q-item
          v-close-popup
          clickable
          @click="emit('edit-point', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="edit" color="info" />
          </q-item-section>
          <q-item-section>Edit Point</q-item-section>
        </q-item>

        <q-separator />

        <q-item
          v-close-popup
          clickable
          @click="emit('delete-point', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="delete" color="negative" />
          </q-item-section>
          <q-item-section>Delete Point</q-item-section>
        </q-item>
      </template>

      <!-- Zone (deprecated type) Context Menu Options -->
      <template v-if="contextMenuNode?.type === 'zone'">
        <q-item
          v-close-popup
          clickable
          @click="emit('add-device', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="add_circle" color="primary" />
          </q-item-section>
          <q-item-section>Add Device</q-item-section>
        </q-item>

        <q-item
          v-close-popup
          clickable
          @click="emit('add-point', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="scatter_plot" color="primary" />
          </q-item-section>
          <q-item-section>Add Point</q-item-section>
        </q-item>

        <q-separator />

        <q-item
          v-close-popup
          clickable
          @click="emit('save-as-template', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="save_as" color="secondary" />
          </q-item-section>
          <q-item-section>Save Zone as Template</q-item-section>
        </q-item>

        <q-item
          v-close-popup
          clickable
          @click="emit('duplicate-zone', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="content_copy" color="purple" />
          </q-item-section>
          <q-item-section>Duplicate Zone</q-item-section>
        </q-item>

        <q-separator />

        <q-item
          v-close-popup
          clickable
          @click="emit('confirm-delete', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="delete" color="negative" />
          </q-item-section>
          <q-item-section>Delete Zone</q-item-section>
        </q-item>
      </template>

      <!-- Controller Context Menu Options (Handle BEFORE equipment) -->
      <template v-if="contextMenuNode?.type === 'equipment' && isController(contextMenuNode)">
        <q-item
          v-close-popup
          clickable
          @click="emit('view-controller-io', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="dashboard" color="primary" />
          </q-item-section>
          <q-item-section>View I/O</q-item-section>
        </q-item>

        <q-item
          v-close-popup
          clickable
          @click="emit('edit-equipment', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="edit" color="info" />
          </q-item-section>
          <q-item-section>Edit Controller</q-item-section>
        </q-item>

        <q-separator />

        <q-item
          v-close-popup
          clickable
          @click="emit('confirm-delete', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="delete" color="negative" />
          </q-item-section>
          <q-item-section>Delete Controller</q-item-section>
        </q-item>
      </template>

      <!-- Equipment Context Menu Options (Skip if controller) -->
      <template v-if="contextMenuNode?.type === 'equipment' && !isController(contextMenuNode)">
        <!-- Add Zone option for AHUs only (not for templated equipment) -->
        <q-item
          v-if="isAHU(contextMenuNode) && !isTemplatedEquipment(contextMenuNode)"
          v-close-popup
          clickable
          @click="emit('add-zone-section', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="view_module" color="primary" />
          </q-item-section>
          <q-item-section>Add Zone Section</q-item-section>
        </q-item>

        <q-item
          v-if="!isTemplatedEquipment(contextMenuNode)"
          v-close-popup
          clickable
          @click="emit('add-device', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="add_circle" color="primary" />
          </q-item-section>
          <q-item-section>Add Device</q-item-section>
        </q-item>

        <q-item
          v-if="!isTemplatedEquipment(contextMenuNode) && canAddPointsToEquipment(contextMenuNode)"
          v-close-popup
          clickable
          @click="emit('add-point', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="scatter_plot" color="primary" />
          </q-item-section>
          <q-item-section>Add Point</q-item-section>
        </q-item>

        <!-- Notification for templated equipment -->
        <q-item
          v-if="isTemplatedEquipment(contextMenuNode)"
          disable
        >
          <q-item-section avatar>
            <q-icon name="info" color="grey" />
          </q-item-section>
          <q-item-section class="text-grey">
            <div>This equipment was created from a template</div>
            <div class="text-caption">Structure cannot be modified directly</div>
          </q-item-section>
        </q-item>

        <q-separator />

        <q-item
          v-if="!isTemplatedEquipment(contextMenuNode)"
          v-close-popup
          clickable
          @click="emit('save-as-template', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="save_as" color="secondary" />
          </q-item-section>
          <q-item-section>Save as Template</q-item-section>
        </q-item>

        <q-item
          v-if="!isTemplatedEquipment(contextMenuNode) || isZoneSection(contextMenuNode)"
          v-close-popup
          clickable
          @click="emit('edit-equipment', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="edit" color="info" />
          </q-item-section>
          <q-item-section>Edit Equipment</q-item-section>
        </q-item>

        <q-item
          v-if="isTemplatedEquipment(contextMenuNode) && hasTemplateUpdate(contextMenuNode)"
          v-close-popup
          clickable
          @click="emit('sync-template', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="sync" color="orange" />
          </q-item-section>
          <q-item-section>Sync with Template</q-item-section>
        </q-item>

        <q-item
          v-if="isTemplatedEquipment(contextMenuNode)"
          v-close-popup
          clickable
          @click="emit('detach-template', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="link_off" color="warning" />
          </q-item-section>
          <q-item-section>Detach from Template</q-item-section>
        </q-item>

        <!-- Zone Section Template Options -->
        <template v-if="isZoneSection(contextMenuNode)">
          <q-separator />

          <q-item
            v-close-popup
            clickable
            @click="emit('rename-zone-section', contextMenuNode)"
          >
            <q-item-section avatar>
              <q-icon name="edit" color="info" />
            </q-item-section>
            <q-item-section>Rename Zone Section</q-item-section>
          </q-item>

          <q-item
            v-close-popup
            clickable
            @click="emit('create-zone-template', contextMenuNode)"
          >
            <q-item-section avatar>
              <q-icon name="save_as" color="secondary" />
            </q-item-section>
            <q-item-section>Save Zone Section as Template</q-item-section>
          </q-item>

          <q-item
            v-close-popup
            clickable
            @click="emit('duplicate-zone', contextMenuNode)"
          >
            <q-item-section avatar>
              <q-icon name="content_copy" color="purple" />
            </q-item-section>
            <q-item-section>Duplicate Zone Section</q-item-section>
          </q-item>
        </template>

        <!-- Controller Assignment Options -->
        <template v-if="!isController(contextMenuNode)">
          <q-separator />
          <q-item
            v-if="!contextMenuNode.data?.assigned_controller_id"
            v-close-popup
            clickable
            @click="emit('assign-controller', contextMenuNode)"
          >
            <q-item-section avatar>
              <q-icon name="settings_input_component" color="purple" />
            </q-item-section>
            <q-item-section>Assign to Controller</q-item-section>
          </q-item>

          <q-item
            v-if="canAddController(contextMenuNode)"
            v-close-popup
            clickable
            @click="emit('add-controller', contextMenuNode)"
          >
            <q-item-section avatar>
              <q-icon name="add_circle" color="green" />
            </q-item-section>
            <q-item-section>Add Controller</q-item-section>
          </q-item>

          <q-item
            v-if="contextMenuNode.data?.assigned_controller_id"
            v-close-popup
            clickable
            @click="emit('unassign-controller', contextMenuNode)"
          >
            <q-item-section avatar>
              <q-icon name="link_off" color="warning" />
            </q-item-section>
            <q-item-section>Unassign from Controller</q-item-section>
          </q-item>
        </template>

        <q-separator />

        <q-item
          v-close-popup
          clickable
          @click="emit('confirm-delete', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="delete" color="negative" />
          </q-item-section>
          <q-item-section>Delete Equipment</q-item-section>
        </q-item>
      </template>

      <!-- Device Context Menu Options -->
      <template v-if="contextMenuNode?.type === 'device'">
        <!-- Compound Device Actions -->
        <q-item
          v-if="!isCompoundDevice(contextMenuNode)"
          v-close-popup
          clickable
          @click="emit('assemble-compound', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="hub" color="purple" />
          </q-item-section>
          <q-item-section>Assemble into Compound</q-item-section>
        </q-item>
        <q-item
          v-if="isCompoundDevice(contextMenuNode)"
          v-close-popup
          clickable
          @click="emit('disassemble-compound', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="call_split" color="orange" />
          </q-item-section>
          <q-item-section>Disassemble Compound</q-item-section>
        </q-item>

        <q-separator />

        <q-item
          v-if="!isTemplatedDevice(contextMenuNode)"
          v-close-popup
          clickable
          @click="emit('add-point', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="scatter_plot" color="primary" />
          </q-item-section>
          <q-item-section>Add Point</q-item-section>
        </q-item>

        <!-- Notification for templated devices -->
        <q-item
          v-if="isTemplatedDevice(contextMenuNode)"
          disable
        >
          <q-item-section avatar>
            <q-icon name="info" color="grey" />
          </q-item-section>
          <q-item-section class="text-grey">
            <div>This device was created from a template</div>
            <div class="text-caption">Structure cannot be modified directly</div>
          </q-item-section>
        </q-item>

        <q-separator />

        <q-item
          v-if="!isTemplatedDevice(contextMenuNode)"
          v-close-popup
          clickable
          @click="emit('save-as-template', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="save_as" color="secondary" />
          </q-item-section>
          <q-item-section>Save as Template</q-item-section>
        </q-item>

        <q-item
          v-if="!isTemplatedDevice(contextMenuNode)"
          v-close-popup
          clickable
          @click="emit('edit-device', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="edit" color="info" />
          </q-item-section>
          <q-item-section>Edit Device</q-item-section>
        </q-item>

        <q-item
          v-close-popup
          clickable
          @click="emit('change-device-location', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="location_on" color="accent" />
          </q-item-section>
          <q-item-section>Change Location</q-item-section>
        </q-item>

        <q-item
          v-if="isTemplatedDevice(contextMenuNode) && hasTemplateUpdate(contextMenuNode)"
          v-close-popup
          clickable
          @click="emit('sync-template', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="sync" color="orange" />
          </q-item-section>
          <q-item-section>Sync with Template</q-item-section>
        </q-item>

        <q-item
          v-if="isTemplatedDevice(contextMenuNode)"
          v-close-popup
          clickable
          @click="emit('detach-template', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="link_off" color="warning" />
          </q-item-section>
          <q-item-section>Detach from Template</q-item-section>
        </q-item>

        <q-separator />

        <q-item
          v-close-popup
          clickable
          @click="emit('confirm-delete', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="delete" color="negative" />
          </q-item-section>
          <q-item-section>Delete Device</q-item-section>
        </q-item>
      </template>

      <!-- Zone Section Context Menu Options -->
      <template v-if="contextMenuNode?.type === 'zoneSection'">
        <q-item
          v-close-popup
          clickable
          @click="emit('add-device', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="memory" color="primary" />
          </q-item-section>
          <q-item-section>Add Device</q-item-section>
        </q-item>

        <q-item
          v-close-popup
          clickable
          @click="emit('add-point', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="sensors" color="cyan" />
          </q-item-section>
          <q-item-section>Add Point</q-item-section>
        </q-item>

        <q-separator />

        <q-item
          v-close-popup
          clickable
          @click="emit('rename-zone-section', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="edit" color="info" />
          </q-item-section>
          <q-item-section>Rename Zone Section</q-item-section>
        </q-item>

        <q-item
          v-close-popup
          clickable
          @click="emit('create-zone-template', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="save_as" color="secondary" />
          </q-item-section>
          <q-item-section>Save as Template</q-item-section>
        </q-item>

        <q-item
          v-close-popup
          clickable
          @click="emit('duplicate-zone', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="content_copy" color="purple" />
          </q-item-section>
          <q-item-section>Duplicate Zone Section</q-item-section>
        </q-item>

        <q-separator />

        <q-item
          v-close-popup
          clickable
          @click="emit('confirm-delete', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="delete" color="negative" />
          </q-item-section>
          <q-item-section>Delete Zone Section</q-item-section>
        </q-item>
      </template>

      <!-- Default/Generic Context Menu Options for any node that doesn't match above -->
      <template v-if="!['building', 'level', 'space', 'point', 'zone', 'equipment', 'device', 'zoneSection'].includes(contextMenuNode?.type) && !isZone(contextMenuNode)">
        <!-- Debug info section -->
        <q-item dense class="bg-grey-2 text-caption">
          <q-item-section>
            <div>Unknown Node Type: {{ contextMenuNode?.type || 'undefined' }}</div>
            <div v-if="contextMenuNode?.subtype">Subtype: {{ contextMenuNode.subtype }}</div>
            <div v-if="contextMenuNode?.data?.brick_class">BRICK: {{ contextMenuNode.data.brick_class }}</div>
          </q-item-section>
        </q-item>
        <q-separator />

        <!-- Generic View Details option -->
        <q-item
          v-close-popup
          clickable
          @click="emit('view-node-details', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="info" color="info" />
          </q-item-section>
          <q-item-section>View Details</q-item-section>
        </q-item>

        <!-- Generic Refresh option -->
        <q-item
          v-close-popup
          clickable
          @click="emit('refresh-node', contextMenuNode)"
        >
          <q-item-section avatar>
            <q-icon name="refresh" color="primary" />
          </q-item-section>
          <q-item-section>Refresh</q-item-section>
        </q-item>

        <!-- Generic Delete option if allowed -->
        <template v-if="canDeleteNode(contextMenuNode)">
          <q-separator />
          <q-item
            v-close-popup
            clickable
            @click="emit('confirm-delete', contextMenuNode)"
          >
            <q-item-section avatar>
              <q-icon name="delete" color="negative" />
            </q-item-section>
            <q-item-section>Delete</q-item-section>
          </q-item>
        </template>
      </template>
    </q-list>
  </q-menu>
</template>

<script setup>
  import { computed } from 'vue';
  import { useTemplateProtection } from '@/composables/brick-schema';
  import { useNodeTypeHelpers } from '@/composables/brick-schema/logic';

  /**
   * BrickContextMenuManager Component
   *
   * Manages the context menu for all node types in the BRICK Schema hierarchy.
   * Provides context-appropriate menu options based on node type, template status,
   * and permissions.
   *
   * @component
   */

  // ============================================================================
  // PROPS
  // ============================================================================

  const props = defineProps({
    /**
     * Whether the context menu should be visible
     */
    modelValue: {
      type: Boolean,
      default: false,
    },

    /**
     * The DOM element that triggered the context menu
     */
    contextMenuTarget: {
      type: [Object, null],
      default: null,
    },

    /**
     * The tree node that was right-clicked
     */
    contextMenuNode: {
      type: [Object, null],
      default: null,
    },

    /**
     * Function to check if a node can be deleted
     */
    canDeleteNode: {
      type: Function,
      required: true,
    },

    /**
     * Function to check if a node can have a controller added
     */
    canAddController: {
      type: Function,
      required: true,
    },

    /**
     * Function to check if a node has a template update available
     */
    hasTemplateUpdate: {
      type: Function,
      required: true,
    },
  });

  // ============================================================================
  // EMITS
  // ============================================================================

  const emit = defineEmits([
    'update:modelValue',
    'add-level',
    'edit-building',
    'delete-building',
    'add-space',
    'edit-level',
    'delete-level',
    'add-equipment-to-space',
    'add-compound-template',
    'add-switchboard',
    'edit-space',
    'delete-space',
    'delete-equipment',
    'edit-point',
    'delete-point',
    'add-device',
    'add-point',
    'save-as-template',
    'duplicate-zone',
    'confirm-delete',
    'view-controller-io',
    'edit-equipment',
    'add-zone-section',
    'sync-template',
    'detach-template',
    'rename-zone-section',
    'create-zone-template',
    'assign-controller',
    'add-controller',
    'unassign-controller',
    'assemble-compound',
    'disassemble-compound',
    'edit-device',
    'change-device-location',
    'view-node-details',
    'refresh-node',
  ]);

  // ============================================================================
  // COMPOSABLES
  // ============================================================================

  // Template protection helpers
  const {
    isController,
    isTemplatedEquipment,
    isTemplatedDevice,
    canAddPointsToEquipment,
  } = useTemplateProtection();

  // Node type helpers
  const {
    isCompoundDevice,
    isAHU,
    isZoneSection,
    isZone,
  } = useNodeTypeHelpers();

  // ============================================================================
  // COMPUTED
  // ============================================================================

  const showContextMenu = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
  });
</script>

<style scoped>
/* Add any component-specific styles here if needed */
</style>
